import React, { createContext, Dispatch, SetStateAction, useContext, useEffect, useMemo, useState } from 'react';
import { moduleClientFactory } from '../../../../api-service/module-client-factory';
import { useTranscription, IUseTranscription } from '../../../../hooks/use-transcription';
import { DialogSupportState, DialogSupportSettings, emptyDialogSupportState, emptyDialogSupportSettings, AUDIO_CHUNK_SIZE_SECONDS, MeetingForm } from './dialog-support-interfaces';

const transcriptionClient = moduleClientFactory.createTranscriptionClientWithoutErrorInterceptor();

interface DialogSupportContextData {
  dialogSupportState: DialogSupportState;
  setDialogSupportState: Dispatch<SetStateAction<DialogSupportState>>;
  transcriptionState: IUseTranscription;
  settings: DialogSupportSettings;
  setSettings: (settings: DialogSupportSettings) => void;
}

const DialogSupportContext = createContext<DialogSupportContextData>({
  dialogSupportState: emptyDialogSupportState,
  setDialogSupportState: () => {},
  transcriptionState: {} as IUseTranscription,
  settings: emptyDialogSupportSettings,
  setSettings: () => {},
});

interface DialogSupportProviderProps {
  children: React.ReactNode;
}

export function DialogSupportProvider({ children }: DialogSupportProviderProps): React.ReactElement {
  const transcriptionState = useTranscription(transcriptionClient, {
    audioChunkSizeSeconds: AUDIO_CHUNK_SIZE_SECONDS,
    pollingTimeoutMs: 40000,
  });

  const [settings, setSettingsInternal] = useState<DialogSupportSettings>(() => {
    const savedMeetingForm = localStorage.getItem('journal-diary-dialog-support-meetingForm');
    const meetingForm = (savedMeetingForm as MeetingForm) || 'Personal';
    const savedDeviceId = localStorage.getItem(`journal-diary-dialog-support-lastSelectedDevice-${meetingForm}`);
    return {
      meetingForm,
      selectedDeviceId: savedDeviceId || undefined,
    };
  });

  function setSettings(newSettings: DialogSupportSettings) {
    setSettingsInternal(newSettings);
    localStorage.setItem('journal-diary-dialog-support-meetingForm', newSettings.meetingForm);
    if (newSettings.selectedDeviceId) {
      localStorage.setItem(`journal-diary-dialog-support-lastSelectedDevice-${newSettings.meetingForm}`, newSettings.selectedDeviceId);
    }
  }

  // Dynamically load device when meeting form changes
  useEffect(() => {
    const savedDeviceId = localStorage.getItem(`journal-diary-dialog-support-lastSelectedDevice-${settings.meetingForm}`);
    if (savedDeviceId && savedDeviceId !== settings.selectedDeviceId) {
      setSettingsInternal(prev => ({ ...prev, selectedDeviceId: savedDeviceId }));
    }
  }, [settings.meetingForm]);

  const [dialogSupportState, setDialogSupportState] = useState<DialogSupportState>(emptyDialogSupportState);

  useEffect(() => {
    setDialogSupportState(prev => ({
      ...prev,
      dialogSupportHasBeenRecording: prev.dialogSupportHasBeenRecording || (transcriptionState.transcriptionStatus === 'active'),
    }));
  }, [transcriptionState.transcriptionStatus]);

  const contextValue = useMemo(
    () => ({
      dialogSupportState,
      setDialogSupportState,
      transcriptionState,
      settings,
      setSettings,
    }),
    [dialogSupportState, transcriptionState, settings]
  );

  return <DialogSupportContext.Provider value={contextValue}>{children}</DialogSupportContext.Provider>;
}

export function useDialogSupport(): DialogSupportContextData {
  return useContext(DialogSupportContext);
}
